import { CatalogResponse, Column, RawColumn, Section } from '../models';
import { Data } from '../models/data';
import { compose } from './compose';

export interface LovMap {
  [key: string]: Data[];
}

/**
 * Process by breaking each entry and re-building with the resultant object
 * of applying a transformer function. ideally a `transformer function` is a function
 * that receives an array of objects and return it's results. ideally the results of a
 * transformer function should be of the same type of objects it receives. Handy to
 * filter, map, reduce operations on CatalogResponses entries
 *
 * @param catalog CatalogResponse
 * @param transformers
 */
export function connectCatalogProcessing<T extends RawColumn = Column>(
  catalog: CatalogResponse<T>,
  ...transformers: Function[]
) {
  const allTransformers = compose<Array<Section<T>>, Array<Section<T>>>(...transformers);
  // break response then map columns with corresponding LOVS `values` and re-build response
  return Object.entries(catalog).reduce(
    // break response
    (acc, [entity, sections]) => ({
      ...acc,
      // rebuild response
      [entity]: allTransformers(sections),
    }),
    {}
  );
}

/**
 * Adds the `values` options to each section columns that has a `lookupCategory` by looking
 * into a lovsMap json object where the keys are the `lookupCategory` value
 * @param catalog
 * @param lovsMap
 */
export function mapCatalogResponseLovsValues<T extends RawColumn = Column>(
  catalog: CatalogResponse<T>,
  lovsMap: LovMap
) {
  // break response then map columns with corresponding LOVS `values` and re-build response
  const mapLovsTransformer = (sections: Array<Section<T>>): Array<Section<T>> =>
    sections.map(sectionLOVWithLookupMap(lovsMap));

  return connectCatalogProcessing(catalog, mapLovsTransformer);
}

/**
 * Adds `values` attribute to each column of section passed
 * @param lovsMap
 * @param column
 */
export const sectionLOVWithLookupMap =
  <T extends RawColumn = Column>(lovsMap: LovMap) =>
  (section: Section<T>) => ({
    ...section,
    columns: section.columns.map(mapOneColumnsLovsValues(lovsMap)),
  });

/**
 * Adds `values` to a type that extends from RawColumn
 * @param lovsMap
 * @param column
 */
export const mapOneColumnsLovsValues =
  <T extends RawColumn = Column>(lovsMap: LovMap) =>
  (column: T): T => ({ ...column, values: lovsMap[column.lookupCategory] ?? column.values });
